import {Injectable} from "@angular/core";

import {ReportError} from "app/app.service";
import {ApiService} from "app/services/domain/api.service";
import * as SharedSvc from "app/services/domain/base.service";
import {AutoRetryRequest} from "app/services/domain/base.service";
import {FilterOptionsExtended} from "app/services/domain/filter-options/filter-options.service";
import {WorkloadDefinitionVersionExtended, WorkloadDefinitionVersionsService} from "app/services/domain/workload-versions.service";
import * as Models from "app/services/proxy/model/models";
import {WorkloadManagementUser} from "app/workloads/workload-management-user";

import {CacheService} from "framework/services/cache.service";
import {ErrorService} from "framework/services/error.service";
import {Lookup, UtilsService} from "framework/services/utils.service";
import {KeyValueChip} from "framework/ui/chips/key-value-chip-list.component";
import {ControlOption} from "framework/ui/control-option";
import {mapInParallelNoNulls} from "framework/utils/concurrency";
import {Memoizer} from "framework/utils/memoizers";

@Injectable()
export class WorkloadDefinitionsService extends SharedSvc.BaseDefinitionService<Models.WorkloadConfiguration, Models.WorkloadDefinition, Models.WorkloadDefinitionVersion, WorkloadDefinitionExtended>
{
    constructor(api: ApiService,
                errors: ErrorService,
                cache: CacheService)
    {
        super(api, errors, cache, Models.WorkloadDefinition, WorkloadDefinitionExtended.newInstance);
    }

    public createDefinition(): Promise<Models.WorkloadDefinition>
    {
        return this.domain.apis.workloadDefinitionsApi.create();
    }

    public setState(definition: string,
                    model: Models.WorkloadDefinition): Promise<Models.WorkloadDefinition>
    {
        return this.domain.apis.workloadDefinitionsApi.setState(definition, model);
    }

    public setStateBulk(model: Models.WorkloadDefinition[]): Promise<Models.WorkloadDefinition[]>
    {
        return this.domain.apis.workloadDefinitionsApi.setStateBulk(model);
    }

    public async remove(definition: string): Promise<boolean>
    {
        return this.api.workloadDefinitionsApi.remove(definition);
    }

    public async removeDefinition(definitionExt: WorkloadDefinitionExtended)
    {
        if (await definitionExt.remove())
        {
            this.domain.workloadsList.resetTenantCache(definitionExt.model.tenant_id);
        }
    }

    public getList(request: Models.WorkloadDefinitionListRequest): Promise<Models.ListResponse>
    {
        return this.api.workloadDefinitionsApi.list(request);
    }

    @ReportError
    public async getAll(): Promise<Models.RecordIdentity[]>
    {
        return null;
    }

    async getAllFlats(workloads: Models.WorkloadDefinition[],
                      requireReleaseVersion: boolean): Promise<WorkloadFlat[]>
    {
        const [filterOptions, userOptions] = await Promise.all([
                                                                   this.domain.filterOptions.getFilterOptions(),
                                                                   this.domain.users.listUsersOptions()
                                                               ]);

        return await mapInParallelNoNulls(workloads, async (def) =>
        {
            const defExt  = WorkloadDefinitionExtended.newInstance(this, def);
            const release = await defExt.getRelease<WorkloadDefinitionVersionExtended>();
            const head    = !requireReleaseVersion && !release ? await defExt.getHead<WorkloadDefinitionVersionExtended>() : null;
            if (!release && !head) return null;
            const flat = new WorkloadFlat(defExt, release, head, filterOptions, userOptions);
            await flat.versionExt?.getDetails();
            return flat;
        });
    }

    sortFilterFlats(flats: WorkloadFlat[],
                    filters: WorkloadFilters,
                    sort: WorkloadSortId,
                    ascending: boolean): WorkloadFlat[]
    {
        if (filters)
        {
            flats = flats.filter((f) =>
                                 {
                                     if (filters.tenantId && f.tenantId !== filters.tenantId)
                                     {
                                         return false;
                                     }

                                     if (filters.requireRelease != null && !f.releaseVersion)
                                     {
                                         return false;
                                     }

                                     if (filters.query && !f.matchesQuery(filters.query))
                                     {
                                         return false;
                                     }

                                     if (filters.owners?.length && !filters.owners.includes(f.owner))
                                     {
                                         return false;
                                     }

                                     return true;
                                 });
        }

        if (sort)
        {
            let sortFn = WorkloadSorts[sort];
            flats.sort((a,
                        b) => UtilsService.compareStrings(sortFn(a), sortFn(b), ascending));
        }

        return flats;
    }

    public getHistory(sysId: string): Promise<Models.WorkloadDefinitionVersion[]>
    {
        return this.api.workloadDefinitionsApi.getHistory(sysId);
    }

    //--//

    protected cachePrefix(): string { return "WorkloadDefinition"; }

    protected getRaw(id: string): Promise<Models.WorkloadDefinition>
    {
        return AutoRetryRequest(20, () => this.api.workloadDefinitionsApi.get(id));
    }

    protected getBatchRaw(ids: string[]): Promise<Models.WorkloadDefinition[]>
    {
        const request = Models.RecordIdentityBatchRequest.newInstance({ids: ids.map(id => WorkloadDefinitionExtended.newIdentity(id))});
        return this.api.workloadDefinitionsApi.batch(request);
    }

    protected canBatch(): boolean
    {
        return true;
    }
}

export class WorkloadDefinitionExtended extends SharedSvc.ExtendedDefinitionModel<Models.WorkloadConfiguration, Models.WorkloadDefinition, Models.WorkloadDefinitionVersion>
{
    static newInstance(svc: WorkloadDefinitionsService,
                       model: Models.WorkloadDefinition): WorkloadDefinitionExtended
    {
        return new WorkloadDefinitionExtended(svc, model, "WorkloadDefinition");
    }

    public static newIdentity(sysId?: string): Models.RecordIdentity
    {
        return Models.RecordIdentity.newInstance({
                                                     table: "WorkloadDefinition",
                                                     sysId: sysId
                                                 });
    }

    public async areFrameworksCompatible(frameworkIds: string[]): Promise<boolean>
    {
        const workloadClouds = await this.getWorkloadClouds();
        if (!workloadClouds?.size) return null;

        const fetcher    = this.domain.wafFrameworks.getFrameworksFetcher(this.domain.users.lastActiveTenantId);
        const frameworks = await mapInParallelNoNulls(frameworkIds, (frameworkId) => fetcher.getFrameworkById(frameworkId));
        return frameworks.every((framework) => framework.valid_service_types.some((cloud) => workloadClouds.has(cloud)));
    }

    public async getWorkloadClouds(): Promise<Set<Models.CloudServiceName>>
    {
        const version    = this.model.releaseVersion || this.model.headVersion;
        const versionExt = version && await this.getVersion(version);
        if (!versionExt) return null;

        const clouds        = new Set<Models.CloudServiceName>();
        const filterOptions = await this.domain.filterOptions.getFilterOptions();

        for (const tier of versionExt.model.details.tiers || [])
        {
            for (const account of tier.cloud_account_id)
            {
                const cloud  = filterOptions.resolveCloudServiceName(account);
                const status = filterOptions.getStatusFromCloudAccount(account);
                if (status == Models.ServiceAccountStatus.active)
                {
                    if (cloud) clouds.add(cloud);
                }
            }
        }

        return clouds;
    }

    public async getWorkloadCloudAccounts(): Promise<Set<string>>
    {
        const version    = this.model.releaseVersion || this.model.headVersion;
        const versionExt = version && await this.getVersion(version);
        if (!versionExt) return null;

        const accounts = new Set<string>();
        for (const tier of versionExt.model.details.tiers || [])
        {
            tier.cloud_account_id.map(acc => accounts.add(acc));
        }
        return accounts;
    }

    //--//

    protected definitionSvc(): WorkloadDefinitionsService
    {
        return this.domain.workloadDefinitions;
    }

    protected definitionVersionSvc(): WorkloadDefinitionVersionsService
    {
        return this.domain.workloadDefinitionVersions;
    }
}

export class WorkloadFlat
{
    public allVersions: Lookup<WorkloadDefinitionVersionExtended>;

    public readonly ownerOption: ControlOption<string>;

    constructor(public readonly definition: WorkloadDefinitionExtended,
                public readonly releaseVersion: WorkloadDefinitionVersionExtended,
                public readonly headVersion: WorkloadDefinitionVersionExtended,
                private readonly m_filterOptions: FilterOptionsExtended,
                userOptions: ControlOption<string>[])
    {
        const ownerId    = this.definition.model.owner || "";
        this.ownerOption = userOptions.find((u) => u.id === ownerId);
    }

    public get id(): string
    {
        return this.definition.model.sysId;
    }

    public get name(): string
    {
        return this.definition.model.name || "";
    }

    public get owner(): string
    {
        return this.ownerOption?.label || "";
    }

    public get versionExt(): WorkloadDefinitionVersionExtended
    {
        return this.releaseVersion || this.headVersion;
    }

    public get workloadType(): string
    {
        return this.definition.model.workload_type;
    }

    public get details(): Models.WorkloadConfiguration
    {
        return this.versionExt?.model.details;
    }

    public get tenantId(): string
    {
        return this.definition.model.tenant_id;
    }

    public canEdit(user: WorkloadManagementUser): boolean
    {
        if (user.isAdmin) return true;
        return user.canUpdate && this.definition.model.owner === user.userId;
    }

    public matchesQuery(query: string): boolean
    {
        if (!query) return true;
        query = query.toLowerCase();

        const name     = this.name.toLowerCase();
        const type     = this.workloadType?.toLowerCase() || "";
        const owner    = this.owner.toLowerCase();
        const accounts = this.getCloudAccounts()
                             .map((a) => a.cloudAccountName.toLowerCase())
                             .join("\n");

        return name.includes(query) || type.includes(query) || accounts.includes(query) || owner.includes(query);
    }

    @Memoizer
    public getCloudAccounts(): WorkloadCloudAccount[]
    {
        const lookup: Lookup<WorkloadCloudAccount> = {};
        for (let tier of this.details.tiers)
        {
            for (let acc of tier.cloud_account_id)
            {
                if (!lookup[acc])
                {
                    let provider = this.m_filterOptions.resolveCloudServiceName(acc);
                    let name     = this.m_filterOptions.resolveAccountId(acc);
                    let status   = this.m_filterOptions.getStatusFromCloudAccount(acc);
                    if (status == Models.ServiceAccountStatus.active)
                    {
                        if (provider && name)
                        {
                            lookup[acc] = {
                                serviceName     : provider,
                                cloudAccountId  : acc,
                                cloudAccountName: name
                            };
                        }
                    }
                }
            }
        }

        return UtilsService.mapLookup(lookup, (key,
                                               value) => value, true);
    }

    @Memoizer
    public getClouds(): Set<Models.CloudServiceName>
    {
        const accounts = this.getCloudAccounts();
        return new Set(accounts.map((a) => a.serviceName));
    }

    @Memoizer
    public getTags(): KeyValueChip[]
    {
        return this.details.tags.map((t) => new KeyValueChip(t.key, t.value, []));
    }
}

export const WorkloadSorts = {
    Name        : (workload: WorkloadFlat) => workload.name,
    Owner       : (workload: WorkloadFlat) => workload.owner,
    WorkloadType: (workload: WorkloadFlat) => workload.workloadType
};

export type WorkloadSortId = keyof typeof WorkloadSorts;

export interface WorkloadFilters
{
    tenantId: string;

    query?: string;

    owners?: string[];

    requireRelease?: boolean;
}

export interface WorkloadCloudAccount
{
    serviceName: Models.CloudServiceName;

    cloudAccountId: string;

    cloudAccountName: string;
}